import React from 'react';
import { NavItem, ServiceItem, StatItem, ProjectItem, TestimonialItem } from './types';
import {
  HardHat, Droplet, Zap, Factory,
  Users, Lightbulb, Shield, ThumbsUp,
  Building2, Cpu, Settings, Flame, Wrench, Award,
  Grid,
  Brush,
  HardHatIcon,
  Hammer,
  Layout
} from 'lucide-react';

export const COMPANY_INFO = {
  name: "Azmi Gulf Company",
  logoChar: "A",
  phone: "+966 56 878 1043",
  phone2: "+966 57 282 0330",
  email: "info@azmigulfcompany.com",
  address: "Al Harith Ibn St, Al Jubail, Kingdom of Saudi Arabia",
  location: "Al Jubail, Saudi Arabia",
  workingHours: "Sun-Thu, 8am - 5pm",
  description: "A leading industrial contractor providing top-tier engineering and construction services across the Kingdom and beyond.",
  social: { facebook: "#", twitter: "#", linkedin: "#", instagram: "#" },
  logoLight: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 60'%3E%3Cpath d='M40 10 L60 50 L20 50 Z' fill='%231e3a8a'/%3E%3Cpath d='M65 10 L85 50 L75 50 L65 30 L55 50 L45 50 Z' fill='%23dc2626'/%3E%3Ctext x='95' y='40' font-family='Arial' font-weight='bold' font-size='24' fill='%231e3a8a'%3EAZMI GULF%3C/text%3E%3C/svg%3E",
  logoDark: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 60'%3E%3Cpath d='M40 10 L60 50 L20 50 Z' fill='%23fbbf24'/%3E%3Cpath d='M65 10 L85 50 L75 50 L65 30 L55 50 L45 50 Z' fill='%23ffffff'/%3E%3Ctext x='95' y='40' font-family='Arial' font-weight='bold' font-size='24' fill='%23ffffff'%3EAZMI GULF%3C/text%3E%3C/svg%3E"
};

export const NAV_ITEMS: NavItem[] = [
  { label: 'Home', href: '#home' },
  { label: 'About Us', href: '#about' },
  {
    label: 'Why Choose Us',
    href: '#why-choose-us',
    children: [
      { label: 'What We Do', href: '#what-we-do' },
      { label: 'Sectors', href: '#sectors' },
      { label: 'Projects', href: '#projects' },
      { label: 'Sustainability', href: '#sustainability' },
    ]
  },
  { label: 'Careers', href: '#careers' },
  { label: 'Contact', href: '#contact' },
];

export const HERO_SLIDES = [
  {
    image: '/images/1.jpg',
    tag: 'Industrial Excellence',
    titlePrefix: 'Building the',
    titleHighlight: 'Future',
    titleSuffix: 'of Industry',
    subtitle: 'We deliver world-class engineering, construction, and industrial services with a commitment to quality, safety, and innovation.'
  },
  {
    image: '/images/2.jpg',
    tag: 'Oil & Gas Expertise',
    titlePrefix: 'Precision Solutions for',
    titleHighlight: 'Energy',
    titleSuffix: 'Sectors',
    subtitle: 'Providing specialized maintenance, fabrication, and engineering services for the oil and gas industry.'
  },
  {
    image: '/images/3.jpg',
    tag: 'Sustainable Power',
    titlePrefix: 'Empowering Growth with',
    titleHighlight: 'Power',
    titleSuffix: 'Systems',
    subtitle: 'Comprehensive solutions for power generation, transmission, and renewable energy infrastructure.'
  }
];

export const ABOUT_DATA = {
  image: "https://picsum.photos/id/1031/800/600",
  experienceYears: "6+",
  experienceText: "Years of Experience",
  title: "Leaders in Industrial",
  titleHighlight: "Engineering & Construction",
  description1: "Azmi Gulf Company is a premier General Contractor specializing in complex industrial and infrastructure projects. Since our inception, we have been a partner of choice for major clients in the Oil & Gas, Power, and Petrochemical sectors.",
  description2: "We leverage world-class expertise and local knowledge to deliver turnkey solutions that meet the highest international standards. Our commitment to the Kingdom's Vision 2030 drives us to innovate, sustain, and excel in every project we undertake.",
  vision: "To be the premier industrial contractor in the region, recognized for our unwavering commitment to quality, innovation, and sustainable growth, while contributing significantly to the development of the communities we serve.",
  mission: "To deliver superior engineering and construction services by upholding the highest standards of safety and quality. We aim to exceed client expectations through operational excellence, fostering a culture of teamwork, and empowering our workforce."
};

export const WHY_CHOOSE_US = [
  {
    icon: Users,
    title: 'Experienced Team',
    description: 'Our diverse team of engineers and specialists brings decades of combined experience to every project.'
  },
  {
    icon: Lightbulb,
    title: 'Innovation Driven',
    description: 'We leverage cutting-edge technology and modern construction methodologies to deliver superior results.'
  },
  {
    icon: Shield,
    title: 'Safety First',
    description: 'We maintain an impeccable safety record with strict adherence to international HSE standards.'
  },
  {
    icon: Award,
    title: 'Client Satisfaction',
    description: 'Our commitment to quality and transparency has earned us the trust of the region’s top industrial clients.'
  }
];

export const CAPABILITIES = [
  {
    title: "Manpower Supply",
    icon: Users,
    image: "/images/manpower.jpg",
    description: "Providing a highly skilled and certified workforce for large-scale industrial projects, ensuring efficiency and project success.",
    features: ["Certified Skilled Labor", "Technical Specialists", "Engineering Support", "On-site Supervision"]
  },
  {
    title: "General Construction",
    icon: Building2,
    image: "/images/General Construction.jpg",
    description: "Our construction division delivers turnkey solutions for large-scale infrastructure, commercial, and industrial projects.",
    features: ["Site Preparation & Earthworks", "Structural Concrete & Steel", "Architectural Finishing", "HVAC & MEP Installations"]
  },
  {
    title: "Electrical & Plumbing",
    icon: Zap,
    image: "/images/Electrical & Plumbing.jpg",
    description: "Comprehensive MEP (Mechanical, Electrical, and Plumbing) services for industrial plants, infrastructure, and commercial builds.",
    features: ["High-Voltage Installations", "Industrial Piping", "Firefighting Systems", "Technical Commissioning"]
  },
  {
    title: "Ceramic Works",
    icon: Grid,
    image: "/images/ceramic.jpg",
    description: "Precision ceramic and tiling installations for commercial complexes, industrial labs, and residential projects with top-tier finishing.",
    features: ["Industrial Tiling", "Custom Ceramic Design", "High-Durability Surfaces", "Expert Craftsmanship"]
  },
  {
    title: "Complete Painting Works",
    icon: Brush,
    image: "/images/painting.jpg",
    description: "Interior, exterior, and industrial-grade protective coating services using the latest application techniques and sustainable materials.",
    features: ["Industrial Epoxy Coating", "Decorative Wall Finishes", "Weather Protection Paint", "Large-scale Exterior Painting"]
  },
  {
    title: "Steel Fixer Works",
    icon: HardHatIcon,
    image: "/images/steelfixing.jpg",
    description: "Expert steel reinforcement services for heavy concrete structures, ensuring maximum structural integrity and adherence to engineering plans.",
    features: ["Reinforced Steel Detailing", "Precise Rebar Fixing", "Heavy Foundation Support", "Quality Load Bearing"]
  },
  {
    title: "Carpenter Works",
    icon: Hammer,
    image: "/images/carpentry.jpg",
    description: "Professional woodworking and formwork services, including high-end interior carpentry and robust construction shuttering.",
    features: ["Custom Interior Joinery", "Formwork & Shuttering", "Aesthetic Wood Finishes", "Structural Timber Framing"]
  },
  {
    title: "Glass & Partition Works",
    icon: Layout,
    image: "/images/glassworks.jpg",
    description: "Modern architectural glass solutions and sleek partition systems for office spaces, showrooms, and high-tech industrial areas.",
    features: ["Tempered Glass Partitions", "Aluminum Frame Systems", "Acoustic Insulation", "Safety Glass Installations"]
  },
  {
    title: "Oil & Gas Services",
    icon: Flame,
    image: "/images/Oil & Gas Services.jpg",
    description: "Providing critical engineering and support services to the upstream and downstream sectors of the oil and gas industry.",
    features: ["Pipeline Construction", "Refinery Maintenance", "Tank Farm Construction", "Shutdown & Turnaround Services"]
  }
];
export const SERVICES: ServiceItem[] = [
  {
    title: 'General Construction',
    description: 'Delivering high-quality infrastructure and building projects across the region.',
    icon: HardHat,
    image: 'https://picsum.photos/id/1/800/600',
  },
  {
    title: 'Manpower Supply',
    description: 'Providing a highly skilled and certified workforce for large-scale industrial projects, ensuring efficiency and project success.',
    icon: Flame,
    image: 'https://picsum.photos/id/2/800/600',
  },
  {
    title: 'Complete Painting Works',
    description: 'Comprehensive solutions for power generation, transmission, and distribution.',
    icon: Zap,
    image: 'https://picsum.photos/id/3/800/600',
  },
  {
    title: 'Industrial Services',
    description: 'Maintenance, fabrication, and support services for industrial facilities.',
    icon: Wrench,
    image: 'https://picsum.photos/id/4/800/600',
  },
];

export const STATS: StatItem[] = [
  { value: '7+', label: 'Years Experience' },
  { value: '50+', label: 'Projects Completed' },
  { value: '700+', label: 'Employees' },
  { value: '100%', label: 'Safety Record' },
];

export const PROJECTS: ProjectItem[] = [
  {
    title: 'Al-Khobar Plant Expansion',
    category: 'Industrial',
    image: 'https://picsum.photos/id/10/600/400',
    client: 'Saudi Aramco',
    location: 'Al Khobar, KSA',
    date: 'March 2023',
    description: 'A comprehensive expansion of the existing processing facility, including the installation of new fractionation units, storage tanks, and associated piping.',
    gallery: ['https://picsum.photos/id/101/1200/800', 'https://picsum.photos/id/102/1200/800']
  },
  {
    title: 'Riyadh Metro Station',
    category: 'Infrastructure',
    image: '/images/metrostation.jpg',
    client: 'Riyadh Development Authority',
    location: 'Riyadh, KSA',
    date: 'November 2022',
    description: 'Construction of a major interchange station for the Riyadh Metro network.',
    gallery: ['https://picsum.photos/id/104/1200/800']
  },
  {
    title: 'Jubail Refinery Maintenance',
    category: 'Oil & Gas',
    image: 'https://picsum.photos/id/12/600/400',
    client: 'SATORP',
    location: 'Jubail, KSA',
    date: 'Ongoing',
    description: 'Long-term maintenance contract for the Jubail Refinery complex.',
    gallery: ['https://picsum.photos/id/107/1200/800']
  },
  {
    title: 'Jeddah Tower Infrastructure',
    category: 'Infrastructure',
    image: 'https://picsum.photos/id/13/600/400',
    client: 'Jeddah Economic Company',
    location: 'Jeddah, KSA',
    date: 'January 2021',
    description: 'Development of the foundational infrastructure for the Jeddah Tower district.',
    gallery: ['https://picsum.photos/id/110/1200/800']
  },
  {
    title: 'Solar Park Phase 2',
    category: 'Renewable Energy',
    image: 'https://picsum.photos/id/14/600/400',
    client: 'ACWA Power',
    location: 'Tabuk, KSA',
    date: 'August 2023',
    description: 'Installation of 50MW photovoltaic solar panels.',
    gallery: ['https://picsum.photos/id/111/1200/800']
  },
  {
    title: 'Yanbu Desalination Plant',
    category: 'Water & Power',
    image: 'https://picsum.photos/id/15/600/400',
    client: 'SWCC',
    location: 'Yanbu, KSA',
    date: 'December 2022',
    description: 'Civil and electromechanical works for a new reverse osmosis desalination unit.',
    gallery: ['https://picsum.photos/id/113/1200/800']
  },
];

export const TESTIMONIALS: TestimonialItem[] = [
  {
    quote: "Azmi Gulf Company demonstrated exceptional professionalism and technical expertise during the expansion project.",
    author: "Ahmed Al-Sayed",
    role: "Project Director",
    company: "Saudi Aramco",
    image: "https://randomuser.me/api/portraits/men/32.jpg"
  },
  {
    quote: "Working with Azmi Gulf Company on our solar park infrastructure was a seamless experience. They delivered the project ahead of schedule.",
    author: "Sarah Johnson",
    role: "COO",
    company: "ACWA Power",
    image: "https://randomuser.me/api/portraits/women/44.jpg"
  },
  {
    quote: "The team's ability to handle complex fabrication requirements while maintaining operational continuity was impressive.",
    author: "Michael Chen",
    role: "Plant Manager",
    company: "SATORP",
    image: "https://randomuser.me/api/portraits/men/67.jpg"
  }
];

export const CAREERS_DATA = {
  benefits: [
    "Competitive Compensation",
    "Professional Development",
    "Inclusive Environment",
    "Safety-First Culture"
  ],
  jobs: [
    { title: "Driver", location: "Riyadh", type: "Full-time" },
    { title: "Technician", location: "Al Khobar", type: "Full-time" },
    { title: "Labour", location: "Jubail", type: "Contract" },
    { title: "Site Supervisor", location: "Jeddah", type: "Full-time" }
  ]
};